import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import StackedBarChart, { type IChartDataset } from '@/shared/components/charts/StackBarChart';
import DateRangeFilter from '@/shared/components/data-display/DateRangeFilter';
import SearchableSelect from '@/shared/components/forms/SearchableSelect';
import Button from '@/shared/components/ui/Button';

import { tokenUsageService } from '../../feature/tokenUsage/services/tokenUsageService';
import type { ITokenUsageOrgDetailsResponse } from '../../feature/tokenUsage/types/tokenUsage';
import { ROUTES } from '../../routes/routeConstants';

type TimeRangeKey = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

type TokenUsageDetailsLocationState = {
  orgName?: string;
  fromDate?: number;
  toDate?: number;
  onlyDefault?: boolean;
  periodLabel?: string;
};

interface ITokenUsageFiltersForm {
  dateFrom: string;
  dateTo: string;
}

function asNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function formatCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) {
    return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(Math.round(n));
}

function formatRangeForCard(label: string): string {
  return label.replace(' - ', ' – ');
}

export const TokenUsageOrgDetailsPage = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();

  const state = (location.state || {}) as TokenUsageDetailsLocationState;

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<ITokenUsageOrgDetailsResponse | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('last_7_days');
  const [appliedCustomRange, setAppliedCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(null);

  const orgName = state.orgName ?? `Org ${orgId ?? '—'}`;

  const { register, watch, setValue, formState } = useForm<ITokenUsageFiltersForm>({
    defaultValues: { dateFrom: '', dateTo: '' },
    mode: 'onChange',
  });

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  const deriveFromTo = useCallback((): { from: number; to: number; label: string } | null => {
    const now = new Date();
    if (timeRange === 'custom') {
      if (!appliedCustomRange?.from || !appliedCustomRange?.to) {
        return null;
      }
      const from = new Date(appliedCustomRange.from).getTime();
      const to = new Date(appliedCustomRange.to).getTime();
      const label =
        appliedCustomRange.from === appliedCustomRange.to
          ? appliedCustomRange.from
          : `${appliedCustomRange.from} - ${appliedCustomRange.to}`;
      return { from, to, label };
    }
    let days = 7;
    if (timeRange === 'last_30_days') {
      days = 30;
    } else if (timeRange === 'last_90_days') {
      days = 90;
    }
    const to = now.getTime();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).getTime();
    const fromStr = new Date(from).toISOString().split('T')[0] || '';
    const toStr = new Date(to).toISOString().split('T')[0] || '';
    return { from, to, label: `${fromStr} - ${toStr}` };
  }, [timeRange, appliedCustomRange]);

  const selectedRangeLabel = useMemo(() => deriveFromTo()?.label ?? '', [deriveFromTo]);
  const canFetch = Boolean(orgId && (timeRange !== 'custom' || appliedCustomRange));

  const fetchDetails = useCallback(async () => {
    if (!orgId || !canFetch) {
      setLoading(false);
      return;
    }
    const range = deriveFromTo();
    if (!range) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { from, to } = range;
      const resp = await tokenUsageService.getTokenUsageOrgDetails({
        orgId,
        fromDate: from,
        toDate: to,
      });
      setDetails(resp);
    } finally {
      setLoading(false);
    }
  }, [orgId, canFetch, deriveFromTo]);

  useEffect(() => {
    void fetchDetails();
  }, [fetchDetails]);

  // If we navigated here from TokenUsagePage with explicit from/to, reflect it as a custom range.
  useEffect(() => {
    if (typeof state.fromDate === 'number' && typeof state.toDate === 'number') {
      const fromStr = new Date(state.fromDate).toISOString().split('T')[0] || '';
      const toStr = new Date(state.toDate).toISOString().split('T')[0] || '';
      if (fromStr && toStr) {
        setTimeRange('custom');
        setValue('dateFrom', fromStr);
        setValue('dateTo', toStr);
        setAppliedCustomRange({ from: fromStr, to: toStr });
      }
    }
    // only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = details?.total;
  const totalTokens = asNumber(totals?.total_tokens ?? 0);
  const totalInput = asNumber(totals?.input_tokens ?? 0);
  const totalOutput = asNumber(totals?.output_tokens ?? 0);
  const uniqueUsers = asNumber(totals?.unique_users ?? 0);

  const inputPct = useMemo(() => {
    if (!totalTokens) {
      return 0;
    }
    return Math.round((totalInput / totalTokens) * 100);
  }, [totalInput, totalTokens]);

  const outputPct = useMemo(() => {
    if (!totalTokens) {
      return 0;
    }
    return Math.round((totalOutput / totalTokens) * 100);
  }, [totalOutput, totalTokens]);

  const byDayDatasets = useMemo<IChartDataset[]>(() => {
    if (!details?.by_day || details.by_day.length === 0) {
      return [];
    }
    const sorted = [...details.by_day]
      .filter((x) => typeof x.day === 'string')
      .sort((a, b) => String(a.day).localeCompare(String(b.day)));
    const input: IChartDataset = { label: 'Input', data: [], color: '#6AA4C0', count: 0 };
    const output: IChartDataset = { label: 'Output', data: [], color: '#3F836C', count: 0 };
    sorted.forEach((r) => {
      const label = String(r.day);
      const inTok = asNumber(r.input_tokens);
      const outTok = asNumber(r.output_tokens);
      input.data.push({ date: label, value: inTok });
      output.data.push({ date: label, value: outTok });
      input.count += inTok;
      output.count += outTok;
    });
    return [input, output];
  }, [details]);

  const byProjectDatasets = useMemo<IChartDataset[]>(() => {
    if (!details?.by_project || details.by_project.length === 0) {
      return [];
    }
    const sorted = [...details.by_project].sort((a, b) =>
      String(a.project_name ?? 'Org Level').localeCompare(String(b.project_name ?? 'Org Level'))
    );
    const input: IChartDataset = { label: 'Input', data: [], color: '#6AA4C0', count: 0 };
    const output: IChartDataset = { label: 'Output', data: [], color: '#3F836C', count: 0 };
    sorted.forEach((r) => {
      const label = String(r.project_name ?? 'Org Level');
      const inTok = asNumber(r.input_tokens);
      const outTok = asNumber(r.output_tokens);
      input.data.push({ date: label, value: inTok });
      output.data.push({ date: label, value: outTok });
      input.count += inTok;
      output.count += outTok;
    });
    return [input, output];
  }, [details]);

  const byUsedForDatasets = useMemo<IChartDataset[]>(() => {
    if (!details?.by_used_for || details.by_used_for.length === 0) {
      return [];
    }
    const featureLabelMap: Record<string, string> = {
      web_test_run: 'Web Test',
      android_test_run: 'Android Test',
      ios_test_run: 'iOS Test',
      api_test_run: 'API Test',
      agent_test_run: 'Agent Test',
      chat: 'Chatbot',
      test_case_gen: 'UI-to-NLP',
      user_story_gen: 'Generate Test Case',
      unknown: 'Unassigned',
      failure_analysis: 'Failure Analysis',
    };
    const sorted = [...details.by_used_for].sort((a, b) =>
      String(a.used_for ?? 'unknown').localeCompare(String(b.used_for ?? 'unknown'))
    );
    const input: IChartDataset = { label: 'Input', data: [], color: '#6AA4C0', count: 0 };
    const output: IChartDataset = { label: 'Output', data: [], color: '#3F836C', count: 0 };
    sorted.forEach((r) => {
      const raw = String(r.used_for ?? 'unknown').trim() || 'unknown';
      const label = featureLabelMap[raw] || raw;
      const inTok = asNumber(r.input_tokens);
      const outTok = asNumber(r.output_tokens);
      input.data.push({ date: label, value: inTok });
      output.data.push({ date: label, value: outTok });
      input.count += inTok;
      output.count += outTok;
    });
    return [input, output];
  }, [details]);

  const byUserDatasets = useMemo<IChartDataset[]>(() => {
    if (!details?.by_user || details.by_user.length === 0) {
      return [];
    }
    const sorted = [...details.by_user]
      .filter((x) => typeof x.triggered_by_name === 'string')
      .sort((a, b) => String(a.triggered_by_name).localeCompare(String(b.triggered_by_name)));
    const input: IChartDataset = { label: 'Input', data: [], color: '#6AA4C0', count: 0 };
    const output: IChartDataset = { label: 'Output', data: [], color: '#3F836C', count: 0 };
    sorted.forEach((r) => {
      const label = String(r.triggered_by_name || '').trim() || 'Automated';
      const inTok = asNumber(r.input_tokens);
      const outTok = asNumber(r.output_tokens);
      input.data.push({ date: label, value: inTok });
      output.data.push({ date: label, value: outTok });
      input.count += inTok;
      output.count += outTok;
    });
    return [input, output];
  }, [details]);

  if (!orgId) {
    return (
      <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-[calc(100vh-4rem)]'>
        <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-6'>
          <div className='text-gray-900 font-semibold'>Missing organization id.</div>
          <div className='mt-3'>
            <Link to={ROUTES.TOKEN_USAGE} className='text-theme-500 hover:opacity-80'>
              Back to Token Usage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-[calc(100vh-4rem)]'>
      <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-6'>
        <div className='flex items-center justify-between gap-4 flex-wrap mb-4'>
          <div className='flex items-center gap-3 min-w-0'>
            <button
              type='button'
              className='px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
              onClick={() => navigate(ROUTES.TOKEN_USAGE)}
            >
              ← Back
            </button>
            <div className='min-w-0'>
              <h1 className='text-2xl font-bold text-gray-900 m-0 truncate' title={orgName}>
                {orgName}
              </h1>

              <p className='text-sm text-gray-500 mt-1 mb-0'>LLM Usage Analytics</p>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <div className='w-full min-w-0 sm:w-48'>
              <SearchableSelect
                value={timeRange}
                onChange={(v) => {
                  const next = v as TimeRangeKey;
                  setTimeRange(next);
                  if (next !== 'custom') {
                    setAppliedCustomRange(null);
                    setValue('dateFrom', '');
                    setValue('dateTo', '');
                  }
                }}
                options={[
                  { value: 'last_7_days', label: 'Last 7 days' },
                  { value: 'last_30_days', label: 'Last 30 days' },
                  { value: 'last_90_days', label: 'Last 90 days' },
                  { value: 'custom', label: 'Custom' },
                ]}
                placeholder='Last 7 days'
                className='rounded-lg! py-2! border! border-gray-200!'
                containerClassName='!space-y-0'
              />
            </div>

            {timeRange === 'custom' ? (
              <DateRangeFilter<ITokenUsageFiltersForm>
                register={register}
                errors={formState.errors}
                dateFromName='dateFrom'
                dateToName='dateTo'
                dateFrom={dateFrom}
                dateTo={dateTo}
                onSearch={() => {
                  if (dateFrom && dateTo) {
                    setAppliedCustomRange({ from: dateFrom, to: dateTo });
                  }
                }}
                onClear={() => {
                  setAppliedCustomRange(null);
                  setValue('dateFrom', '');
                  setValue('dateTo', '');
                  setTimeRange('last_7_days');
                }}
                className='shrink-0'
              />
            ) : null}

            <Button
              variant='ghost'
              size='icon'
              onClick={fetchDetails}
              disabled={!canFetch}
              aria-label='Refresh usage'
            >
              <RefreshCw />
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5'>
          <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-4'>
            <div className='text-xs text-gray-500 font-semibold'>TOTAL TOKENS</div>
            <div className='mt-2 text-2xl font-bold text-gray-900'>
              {formatCompact(totalTokens)}
            </div>
            <div className='mt-1 text-sm text-gray-500'>
              {formatRangeForCard(selectedRangeLabel)}
            </div>
          </div>
          <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-4'>
            <div className='text-xs text-gray-500 font-semibold'>INPUT TOKENS</div>
            <div className='mt-2 text-2xl font-bold text-gray-900'>{formatCompact(totalInput)}</div>
            <div className='mt-1 text-sm text-gray-500'>{inputPct}% of total</div>
          </div>
          <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-4'>
            <div className='text-xs text-gray-500 font-semibold'>OUTPUT TOKENS</div>
            <div className='mt-2 text-2xl font-bold text-gray-900'>
              {formatCompact(totalOutput)}
            </div>
            <div className='mt-1 text-sm text-gray-500'>{outputPct}% of total</div>
          </div>
          <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-4'>
            <div className='text-xs text-gray-500 font-semibold'>ACTIVE USERS</div>
            <div className='mt-2 text-2xl font-bold text-gray-900'>
              {uniqueUsers.toLocaleString()}
            </div>
            <div className='mt-1 text-sm text-gray-500'>Contributing this period</div>
          </div>
        </div>

        {loading && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className='card animate-pulse border-none p-6'>
                <div className='h-[clamp(160px,30vh,300px)]'></div>
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {byDayDatasets.length > 0 && (
              <StackedBarChart
                title='Token Usage by Day'
                subtitle={`Total: ${formatCompact(byDayDatasets.reduce((s, d) => s + d.count, 0))} tokens`}
                datasets={byDayDatasets}
                height='clamp(160px,30vh,300px)'
                className='h-fit'
                maxBarThickness={40}
                barPercentage={0.6}
                categoryPercentage={0.7}
              />
            )}
            {byProjectDatasets.length > 0 && (
              <StackedBarChart
                title='Token Usage by Project'
                subtitle='Top 5 projects'
                datasets={byProjectDatasets}
                height='clamp(160px,30vh,300px)'
                className='h-fit'
                maxBarThickness={40}
                barPercentage={0.6}
                categoryPercentage={0.7}
              />
            )}
            {byUsedForDatasets.length > 0 && (
              <StackedBarChart
                title='Token Usage by Feature'
                subtitle='All features'
                datasets={byUsedForDatasets}
                height='clamp(160px,30vh,300px)'
                className='h-fit'
                maxBarThickness={40}
                barPercentage={0.6}
                categoryPercentage={0.7}
              />
            )}
            {byUserDatasets.length > 0 && (
              <StackedBarChart
                title='Token Usage by User'
                subtitle='All users'
                datasets={byUserDatasets}
                height='clamp(160px,30vh,300px)'
                className='h-fit'
                maxBarThickness={40}
                barPercentage={0.6}
                categoryPercentage={0.7}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenUsageOrgDetailsPage;
